
import { MessageSquare, User } from "lucide-react";

interface ChatMessageProps {
  content: string;
  role: 'user' | 'assistant';
}

export const ChatMessage = ({ content, role }: ChatMessageProps) => {
  // Function to format the content with better styling
  const formatContent = (text: string) => {
    // Handle bold text formatting by replacing **text** with <strong> tags
    const formatBoldText = (line: string) => {
      return line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    };

    // Split content into sections based on step numbers (Step 1:, Step 2:, etc.)
    const sections = text.split(/(?=Step \d+:|^\d+\.)/).filter(Boolean);
    
    if (sections.length <= 1) {
      // If no numbered sections, split by line breaks for better readability
      return text.split('\n').map((line, i) => {
        if (line.includes('Example:')) {
          const [prefix, example] = line.split('Example:');
          return (
            <div key={i} className="mt-2">
              <span dangerouslySetInnerHTML={{ __html: formatBoldText(prefix) }} />
              <span className="font-medium">Example:</span>
              <span dangerouslySetInnerHTML={{ __html: formatBoldText(example) }} />
            </div>
          );
        }
        return <p key={i} className="mt-2" dangerouslySetInnerHTML={{ __html: formatBoldText(line) }} />;
      });
    }

    return sections.map((section, index) => {
      // Extract the step title and content
      const stepMatch = section.match(/^(Step \d+:|^\d+\.)(.*?)(?=\n|$)/);
      let title = section;
      let content = '';
      
      if (stepMatch) {
        title = stepMatch[1] + stepMatch[2];
        content = section.slice(stepMatch[0].length).trim();
      }

      const formattedContent = content.split('\n').map((line, i) => {
        if (line.includes('Example:')) {
          const [prefix, example] = line.split('Example:');
          return (
            <div key={i} className="mt-3">
              <span dangerouslySetInnerHTML={{ __html: formatBoldText(prefix) }} />
              <span className="font-medium">Example:</span>
              <span dangerouslySetInnerHTML={{ __html: formatBoldText(example) }} />
            </div>
          );
        }
        return <p key={i} className="mt-3" dangerouslySetInnerHTML={{ __html: formatBoldText(line) }} />;
      });

      return (
        <div key={index} className="mb-6">
          <div className="font-medium text-lg mb-3" dangerouslySetInnerHTML={{ __html: formatBoldText(title) }} />
          <div className="ml-4 space-y-2">
            {formattedContent}
          </div>
        </div>
      );
    });
  };

  return (
    <div className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'} mb-4`}>
      <div
        className={`flex gap-3 max-w-[80%] rounded-lg p-6 shadow-sm transition-all ${
          role === 'user'
            ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white'
            : 'bg-white border border-purple-100 dark:bg-gray-800 hover:border-purple-200'
        }`}
      >
        {role === 'assistant' && (
          <MessageSquare className="h-5 w-5 mt-1 text-purple-500 flex-shrink-0" />
        )}
        <div className="flex-1">
          {formatContent(content)}
        </div>
        {role === 'user' && (
          <User className="h-5 w-5 mt-1 text-purple-200 flex-shrink-0" />
        )}
      </div>
    </div>
  );
};
